import {
    BrokerCounts,
    BrokerDetails,
    BrokerListQuery,
    BrokerListResponse,
    ClientMyBroker,
    BrokerStatus,
    BrokerType,
    ClientRateConf,
    LookupBrokersResponse,
    NumeoRestError,
    SearchBrokerRateConfResult,
} from '@numeo/types-core'
import axios, { isAxiosError } from 'axios'
import { getApiBaseUrlWithoutV1 } from 'setup/SetupAxios'

interface ICreateBroker {
    companyName?: string
    mcNumber: string
    // Support both single and array formats for backward compatibility
    fullName?: string
    email?: string
    phone?: string
    // New array-based fields
    contacts?: Array<{ fullName: string; email?: string; phone?: string }>
    emails?: string[]
    additionalUpdateEmails?: string[]
    website?: string
    comment?: string
    type?: BrokerType
}

interface IUpdateBroker extends Partial<ICreateBroker> {
    status?: BrokerStatus
    // Server stamps flaggedAt/flaggedBy; client only sends the intent + reason.
    redFlag?: { flagged: boolean; reason?: string }
}

interface DeleteBrokersResult {
    deletedIds: string[]
    deletedCount: number
}

interface NotifyBrokersRequest {
    emails: string[]
    subject: string
    message: string
}

interface SearchBrokersResponse {
    broker: BrokerDetails
    rateConfs: SearchBrokerRateConfResult[]
}

class MyBrokersApi {
    private readonly BROKERS_URL = `/my-brokers`

    async createBroker(data: ICreateBroker): Promise<ClientMyBroker> {
        try {
            const response = await axios.post<ClientMyBroker>(this.BROKERS_URL, data)
            return response.data
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to create broker', error.response?.data.code || 500)
            }
            throw new NumeoRestError('Unknown error while creating broker', 500)
        }
    }

    async updateBroker(id: string, data: IUpdateBroker): Promise<ClientMyBroker> {
        try {
            const response = await axios.put<ClientMyBroker>(`${this.BROKERS_URL}/${id}`, data)
            return response.data
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to update broker', error.response?.data.code || 500)
            }
            throw new NumeoRestError('Unknown error while updating broker', 500)
        }
    }

    async deleteBrokers(ids: string[]): Promise<DeleteBrokersResult> {
        try {
            const response = await axios.delete<{ success: boolean; data: DeleteBrokersResult }>(`${this.BROKERS_URL}/delete/multiple`, { data: { ids } })
            return response.data.data
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to delete brokers', error.response?.data.code || 500)
            }
            throw new NumeoRestError('Unknown error while deleting brokers', 500)
        }
    }

    private profilesUrl(path: string): string {
        return `${getApiBaseUrlWithoutV1()}/dispatcher/broker-profiles/${path}`
    }

    async lookupBrokers(mcNumbers: string[]): Promise<ClientMyBroker[]> {
        try {
            const response = await axios.post<{ success: boolean; data: LookupBrokersResponse }>(this.profilesUrl('lookups/v1'), { mcNumbers })
            return response.data.data.items
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to look up brokers', error.response?.status || 500)
            }
            throw new NumeoRestError('Unknown error while looking up brokers', 500)
        }
    }

    async listBrokers(query: BrokerListQuery, signal?: AbortSignal): Promise<BrokerListResponse> {
        try {
            const params = {
                page: query.page,
                limit: query.limit,
                q: query.q || undefined,
                type: query.type?.length ? query.type.join(',') : undefined,
                status: query.status?.length ? query.status.join(',') : undefined,
                ids: query.ids?.length ? query.ids.join(',') : undefined,
                brokerIds: query.brokerIds?.length ? query.brokerIds.join(',') : undefined,
                redFlagged: query.redFlagged ? 'true' : undefined,
            }
            const response = await axios.get<{ success: boolean; data: BrokerListResponse }>(this.profilesUrl('list/v1'), { params, signal })
            return response.data.data
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to list brokers', error.response?.status || 500)
            }
            throw new NumeoRestError('Unknown error while listing brokers', 500)
        }
    }

    async countBrokers(): Promise<BrokerCounts> {
        try {
            const response = await axios.get<{ success: boolean; data: BrokerCounts }>(this.profilesUrl('counts/v1'))
            return response.data.data
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to count brokers', error.response?.status || 500)
            }
            throw new NumeoRestError('Unknown error while counting brokers', 500)
        }
    }

    async getBrokerById(id: string): Promise<ClientMyBroker> {
        try {
            const response = await axios.get<ClientMyBroker>(`${this.BROKERS_URL}/${id}`)
            return response.data
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to get broker', error.response?.data.code || 500)
            }
            throw new NumeoRestError('Unknown error while getting broker', 500)
        }
    }

    async getBrokerByMcNumber(mcNumber: string): Promise<ClientMyBroker> {
        try {
            const response = await axios.get<ClientMyBroker>(`${this.BROKERS_URL}/by-mc/${mcNumber}`)
            return response.data
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to get all brokers', error.response?.data.code || 500)
            }
            throw new NumeoRestError('Unknown error while getting all brokers', 500)
        }
    }

    async notifyBrokers(data: NotifyBrokersRequest): Promise<void> {
        try {
            await axios.post(`${this.BROKERS_URL}/notify`, data)
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to notify brokers', error.response?.data.code || 500)
            }
            throw new NumeoRestError('Unknown error while notifying brokers', 500)
        }
    }

    async getRateConfsByMcNumber(mcNumber: string): Promise<ClientRateConf[]> {
        try {
            const response = await axios.get(`/broker-analytics/rateconf/by-mc/${mcNumber}`)
            return response.data.results
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to get rate confirmations by mc number', error.response?.data.code || 500)
            }
            throw new NumeoRestError('Unknown error while getting rate confirmations by mc number', 500)
        }
    }

    async searchBrokers(city: string, state: string, latitude: number, longitude: number): Promise<SearchBrokersResponse[]> {
        try {
            const response = await axios.get(`${this.BROKERS_URL}/search`, {
                params: {
                    city,
                    state,
                    latitude,
                    longitude,
                },
            })
            return response.data.data
        } catch (error) {
            if (isAxiosError(error)) {
                throw new NumeoRestError(error.response?.data.message || 'Failed to get rate confirmations by mc number', error.response?.data.code || 500)
            }
            throw new NumeoRestError('Unknown error while getting rate confirmations by mc number', 500)
        }
    }
}

// Export singleton instance
export const brokersApi = new MyBrokersApi()
